import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Lock, LogOut, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";
import { SUPPORT_TYPE_OPTIONS } from "@/constants/site";

const STATUS_OPTIONS = ["new", "contacted", "archived"];

const SUPPORT_LABEL = Object.fromEntries(
  SUPPORT_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/submissions");
      setSubmissions(data);
    } catch (e) {
      if (e.response?.status === 401) {
        setUser(false);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        load();
      })
      .catch(() => setUser(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Welcome back.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    setSubmissions([]);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/submissions/${id}`, { status });
      setSubmissions((list) => list.map((s) => (s.id === id ? { ...s, status } : s)));
      toast.success(`Marked ${status}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center" data-testid="admin-loading">
        <p className="font-display text-xl italic text-muted-foreground">Checking session…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[85vh] items-center justify-center px-6 py-32">
        <FadeUp>
          <form
            onSubmit={login}
            data-testid="admin-login-form"
            className="grain-card w-full max-w-sm space-y-6 bg-card p-10"
          >
            <div className="space-y-2 text-center">
              <Lock className="mx-auto h-8 w-8 text-sage-600" strokeWidth={1.5} />
              <h1 className="font-display text-3xl font-light text-sage-900">Practice admin</h1>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Private area. Enquiries are stored securely and shown only here.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@practice.com"
                className="rounded-xl border-sage-600/20"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="rounded-xl border-sage-600/20"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full rounded-full bg-sage-600 py-5 text-background hover:bg-sage-700"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </FadeUp>
      </div>
    );
  }

  const filtered = filter === "all" ? submissions : submissions.filter((s) => s.status === filter);

  return (
    <div className="container-x pb-24 pt-32 md:pt-40" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Practice admin</p>
          <h1 className="font-display text-4xl font-light tracking-tight text-sage-900">
            Intake enquiries
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            data-testid="admin-refresh"
            onClick={load}
            className="rounded-full border-sage-600/25 text-sage-700 hover:bg-secondary"
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button
            variant="outline"
            data-testid="admin-logout"
            onClick={logout}
            className="rounded-full border-sage-600/25 text-sage-700 hover:bg-secondary"
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </div>

      <div className="mt-8 flex gap-2">
        {["all", ...STATUS_OPTIONS].map((f) => (
          <button
            key={f}
            data-testid={`admin-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-4 py-1.5 text-xs capitalize transition-colors duration-200 ${
              filter === f
                ? "border-sage-600 bg-sage-600 text-background"
                : "border-sage-600/20 text-muted-foreground hover:border-sage-600/50"
            }`}
          >
            {f}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <Inbox className="h-4 w-4" /> {filtered.length} enquir{filtered.length === 1 ? "y" : "ies"}
        </span>
      </div>

      <div className="grain-card mt-6 overflow-x-auto bg-card" data-testid="admin-submissions-table">
        <Table>
          <TableHeader>
            <TableRow className="border-sage-600/10">
              <TableHead className="text-sage-700">Received</TableHead>
              <TableHead className="text-sage-700">Name</TableHead>
              <TableHead className="text-sage-700">Support</TableHead>
              <TableHead className="text-sage-700">Format</TableHead>
              <TableHead className="text-sage-700">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-14 text-center text-sm text-muted-foreground">
                  No enquiries yet. New submissions from the intake form will appear here.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((s) => (
              <Fragment key={s.id}>
                <TableRow
                  data-testid={`admin-submission-row-${s.id}`}
                  onClick={() => setOpenId((o) => (o === s.id ? null : s.id))}
                  className="cursor-pointer border-sage-600/10 hover:bg-secondary/50"
                >
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {fmtDate(s.created_at)}
                  </TableCell>
                  <TableCell className="text-sm font-medium text-foreground">
                    {s.first_name} {s.last_name}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {SUPPORT_LABEL[s.support_type] || s.support_type}
                  </TableCell>
                  <TableCell className="text-sm capitalize text-muted-foreground">
                    {s.format}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select value={s.status} onValueChange={(v) => setStatus(s.id, v)}>
                      <SelectTrigger
                        data-testid={`admin-status-select-${s.id}`}
                        className={`h-8 w-32 rounded-full border-sage-600/20 text-xs capitalize ${
                          s.status === "new" ? "bg-clay-light/50" : "bg-secondary/60"
                        }`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl border-sage-600/15 bg-card">
                        {STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o} className="capitalize">
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
                {openId === s.id && (
                  <TableRow key={`${s.id}-detail`} className="border-sage-600/10 bg-secondary/30">
                    <TableCell colSpan={5}>
                      <div className="grid gap-6 py-3 md:grid-cols-2">
                        <div className="space-y-3 text-sm">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sage-600">Contact</p>
                          <p className="text-foreground/85">{s.email}</p>
                          <p className="text-foreground/85">{s.phone || "No phone provided"}</p>
                          <p className="text-foreground/85">Availability: {s.availability || "—"}</p>
                        </div>
                        <div className="space-y-3 text-sm">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sage-600">What they shared</p>
                          <p className="text-foreground/85">{(s.reasons || []).join(", ") || "—"}</p>
                          <p className="whitespace-pre-line leading-relaxed text-foreground/85">
                            {s.reasons_detail || "No additional detail."}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Admin;
